import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';

import { Region, SharedComponentSet } from '@paragon/contract-library';
import { designService, sealingService, userService } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch, sortBy } from '@paragon/utilities';

import { useAppSelector } from '../../hooks';
import { CreateSharedComponentSet } from './CreateSharedComponentSet';
import { SelectSharedComponentSet } from './SelectSharedComponentSet';
import { SharingOperations } from './SharingOperations';

const CONTEXT_ERROR = 'Share dialog opened outside of project or shared component set context';

export function ShareDialog() {
  const openContext = useAppSelector((state) => state.application.openContext);
  const userOrganization = useAppSelector((state) => getLoggedInUser(state)?.organization);

  const sharedComponentSetFromContext = useAppSelector((state) => state.sharedComponentSet.sharedComponentSet);

  const [chosenSharedComponentSet, setChosenSharedComponentSet] = useState<SharedComponentSet | null>(
    sharedComponentSetFromContext,
  );
  const [activeSelectionMethod, setActiveSelectionMethod] = useState<null | 'create' | 'select'>(null);

  const [sharedComponentSetsForProject, setSharedComponentSetsForProject] = useState<
    'loading' | 'error' | readonly SharedComponentSet[]
  >('loading');

  const [visibleOrganizations, setVisibleOrganizations] = useState<'loading' | 'error' | readonly string[]>('loading');
  const [regions, setRegions] = useState<'loading' | 'error' | readonly Region[]>('loading');

  useEffect(() => {
    if (openContext?.type !== 'project') return;

    designService.sharedComponentSets
      .fetchForProject(openContext.projectGuid)
      .then((loadedSharedComponentSetsForProject) => {
        setSharedComponentSetsForProject(
          sortBy(
            loadedSharedComponentSetsForProject,
            (sharedComponentSet) => new Date(sharedComponentSet.created),
            'desc',
          ),
        );

        if (loadedSharedComponentSetsForProject.length === 0) setActiveSelectionMethod('create');
      })
      .catch((error) => {
        console.error(error);
        setSharedComponentSetsForProject('error');
      });
  }, [openContext]);

  useEffect(() => {
    if (userOrganization) {
      userService.organizations
        .pGetOrganization(userOrganization)
        .then((organization) => {
          setVisibleOrganizations(organization.visibleOrganizations);
        })
        .catch(() => {
          setVisibleOrganizations('error');
        });

      sealingService.regions
        .fetch()
        .catch(() => 'error' as const)
        .then(setRegions);
    }
  }, [userOrganization]);

  if (openContext?.type !== 'project' && openContext?.type !== 'shared-component-set') throw new Error(CONTEXT_ERROR);

  if (chosenSharedComponentSet) {
    return (
      <SharingOperations
        sharedComponentSet={chosenSharedComponentSet}
        regions={regions}
        visibleOrganizations={visibleOrganizations}
        onNavigationBackRequested={
          openContext?.type === 'project' ? () => void setChosenSharedComponentSet(null) : undefined
        }
      />
    );
  }

  switch (activeSelectionMethod) {
    case null: {
      return (
        <Dialog
          variant="design"
          title="Share"
          onCancel="close"
          hasTransition={false}
          contentSx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Button fullWidth variant="contained" onClick={() => void setActiveSelectionMethod('create')}>
            Create New Shared Component Set
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => void setActiveSelectionMethod('select')}
            disabled={sharedComponentSetsForProject === 'error'}
          >
            Choose Existing Component Set
          </Button>

          {sharedComponentSetsForProject === 'error' && (
            <Alert variant="outlined" severity="error">
              Error loading shared component sets.
            </Alert>
          )}
        </Dialog>
      );
    }

    case 'create':
      return (
        <CreateSharedComponentSet
          setSharedComponentSet={setChosenSharedComponentSet}
          onNavigationBackRequested={() => void setActiveSelectionMethod(null)}
        />
      );

    case 'select':
      if (openContext?.type !== 'project') throw new Error(CONTEXT_ERROR);

      switch (sharedComponentSetsForProject) {
        case 'loading':
          return (
            <Dialog insistent>
              <CircularProgress />
            </Dialog>
          );

        case 'error':
          return (
            <Dialog variant="design" cancelText="Close" onCancel="close">
              <Alert variant="outlined" severity="error">
                Failed to load shared component sets. Please try again, or contact Paragon Customer Support.
              </Alert>
            </Dialog>
          );

        default:
          return (
            <SelectSharedComponentSet
              sharedComponentSets={sharedComponentSetsForProject}
              setSharedComponentSet={setChosenSharedComponentSet}
              onNavigationBackRequested={() => void setActiveSelectionMethod(null)}
            />
          );
      }

    default:
      assertExhaustiveSwitch(activeSelectionMethod);
  }
}
